import moment from 'moment'
import type { LocationOpenHoursInput } from 'app/generated/gql/graphql'

/**
 * Pure helpers for the customer-facing AI Agent Settings overview: what the
 * practice sees about its agent, shaped from the config and location records.
 */

/** What the agent says when the practice has not written a greeting of its own. */
export const DEFAULT_GREETING = `Thanks for calling {{businessName}}, this is {{botname}}. How can I help you today?`

/** The greeting is stored as a template; the agent speaks it with these filled in. */
export function renderGreeting(
  greeting: string | null | undefined,
  businessName: string | null | undefined,
  agentName: string | null | undefined,
): string {
  if (!greeting) return ``
  return greeting.replace(/\{\{businessName\}\}/g, businessName ?? ``).replace(/\{\{botname\}\}/g, agentName ?? ``)
}

type OpenHoursRangeLike = { from: string; to: string }
type OpenDayLike = { open?: boolean | null; hours?: OpenHoursRangeLike[] | null } | null | undefined
export type OpenHoursLike = Partial<Record<DayKey, OpenDayLike>> | null | undefined

type DayKey = `monday` | `tuesday` | `wednesday` | `thursday` | `friday` | `saturday` | `sunday`

const WEEK: { key: DayKey; label: string }[] = [
  { key: `monday`, label: `Mon` },
  { key: `tuesday`, label: `Tue` },
  { key: `wednesday`, label: `Wed` },
  { key: `thursday`, label: `Thu` },
  { key: `friday`, label: `Fri` },
  { key: `saturday`, label: `Sat` },
  { key: `sunday`, label: `Sun` },
]

export type HoursLine = { day: string; hours: string }

/** "08:00" reads as "8:00 AM"; anything that is not HH:mm passes through. */
export function formatClock(time: string): string {
  const parsed = moment(time, `HH:mm`, true)
  return parsed.isValid() ? parsed.format(`h:mm A`) : time
}

/** Monday to Sunday, one line each, closed days included so the week reads whole. */
export function formatOpenHours(openHours: OpenHoursLike): HoursLine[] {
  return WEEK.map(({ key, label }) => {
    const day = openHours?.[key]
    const ranges = day?.open ? (day.hours ?? []) : []
    return {
      day: label,
      hours: ranges.length ? ranges.map((r) => `${formatClock(r.from)} to ${formatClock(r.to)}`).join(`, `) : `Closed`,
    }
  })
}

export const BOOKING_CATEGORIES = [
  { key: `general`, label: `General visits` },
  { key: `emergency`, label: `Emergency visits` },
  { key: `consultation`, label: `Consultations` },
  { key: `limitedExam`, label: `Limited exams` },
] as const

export type BookingCategoryKey = (typeof BOOKING_CATEGORIES)[number][`key`]

type AppointmentTypeLike = {
  id?: string | null
  alias?: string | null
  displayName?: string | null
  name?: string | null
  appointmentLength?: number | null
}

export type BookedType = { key: BookingCategoryKey; label: string; typeName?: string; minutes?: number }

/**
 * Which practice appointment type each category books as. The config stores
 * appointment type record ids; a category with no match reads as not offered.
 */
export function resolveBookedTypes(
  config:
    | {
        generalApptId?: string | null
        emergencyApptId?: string | null
        consultApptId?: string | null
        limitedExamApptId?: string | null
      }
    | null
    | undefined,
  appointmentTypes: AppointmentTypeLike[],
): BookedType[] {
  const ids: Record<BookingCategoryKey, string | null | undefined> = {
    general: config?.generalApptId,
    emergency: config?.emergencyApptId,
    consultation: config?.consultApptId,
    limitedExam: config?.limitedExamApptId,
  }
  return BOOKING_CATEGORIES.map(({ key, label }) => {
    const type = ids[key] ? appointmentTypes.find((t) => t.id === ids[key]) : undefined
    return {
      key,
      label,
      typeName: type ? type.alias || type.displayName || type.name || undefined : undefined,
      minutes: type?.appointmentLength ?? undefined,
    }
  })
}

/** The number a call is handed to, then the office line, with the same fallbacks the ops page uses. */
export function resolveAgentPhones(
  location:
    | {
        officeTransferNumber?: string | null
        officeMainNumber?: string | null
        businessPhone?: string | null
        phone?: string | null
      }
    | null
    | undefined,
): { transfer?: string; main?: string } {
  return {
    transfer: location?.officeTransferNumber || location?.officeMainNumber || location?.businessPhone || undefined,
    main: location?.officeMainNumber || location?.businessPhone || location?.phone || undefined,
  }
}

export type AgentStatusTone = `online` | `redirecting` | `interrupted` | `offline`

export const TONE_WORDS: Record<AgentStatusTone, string> = {
  online: `Online`,
  redirecting: `Redirecting to Office`,
  interrupted: `System Interruption`,
  offline: `Offline`,
}
export type AgentStatusLine = { label: string; tone: AgentStatusTone }

/** The global `ai-agent-enabled` toggle: off means every voice agent is rerouted to its office. */
export const AI_AGENT_GLOBAL_TOGGLE = `ai-agent-enabled`

/**
 * The two status lines in the My Agent header. Voice follows the call routing: the
 * global kill switch first (an emergency reroute of every agent), then the
 * location's live flag, which sends calls to the office when off. SMS has no
 * office to fall back to, so it is simply online or offline.
 */
export function agentStatusLines(input: { voiceLive: boolean; smsOn: boolean; globallyEnabled: boolean }): {
  voice: AgentStatusLine
  sms: AgentStatusLine
} {
  const voice: AgentStatusLine = !input.globallyEnabled
    ? { label: `System Interruption, Redirecting to Office`, tone: `interrupted` }
    : input.voiceLive
      ? { label: `Voice Agent Online`, tone: `online` }
      : { label: `Voice Agent Redirecting to Office`, tone: `redirecting` }
  const sms: AgentStatusLine = input.smsOn
    ? { label: `SMS Agent Online`, tone: `online` }
    : { label: `SMS Agent Offline`, tone: `offline` }
  return { voice, sms }
}

/** Days in the status history strip under each agent's current status. */
export const STATUS_HISTORY_DAYS = 30

/** A stretch of one day the agent was not online, in minutes after midnight (location time). */
export type StatusOutage = { tone: AgentStatusTone; fromMinute: number; toMinute: number }

/** One day of the strip: its most serious state, and every stretch it was not online. */
export type StatusHistoryDay = { date: string; tone: AgentStatusTone; outages: StatusOutage[] }

const SEVERITY: AgentStatusTone[] = [`online`, `offline`, `redirecting`, `interrupted`]

/** The most serious state among a day's outages, which is the colour its bar takes. */
export function worstTone(outages: StatusOutage[]): AgentStatusTone {
  return outages.reduce<AgentStatusTone>(
    (worst, outage) => (SEVERITY.indexOf(outage.tone) > SEVERITY.indexOf(worst) ? outage.tone : worst),
    `online`,
  )
}

const FULL_DAY = 24 * 60

/** When an outage ran and for how long, e.g. "1:05 PM to 5:15 PM, 4h 10m", or "All day". */
export function describeOutageWindow(outage: StatusOutage): string {
  if (outage.fromMinute <= 0 && outage.toMinute >= FULL_DAY) return `All day`
  const clock = (minute: number) => moment().startOf(`day`).add(minute, `minutes`).format(`h:mm A`)
  const length = outage.toMinute - outage.fromMinute
  const hours = Math.floor(length / 60)
  const minutes = length % 60
  const duration = [hours && `${hours}h`, minutes && `${minutes}m`].filter(Boolean).join(` `)
  return `${clock(outage.fromMinute)} to ${clock(outage.toMinute)}, ${duration}`
}

/**
 * Made-up daily status history, oldest first, ending today on the agent's current tone. Some
 * outages last part of a day and some all of it. The API keeps no history of agent status yet;
 * this stands in so the strip can be reviewed.
 */
export function buildSampleStatusHistory(
  channel: `voice` | `sms`,
  current: AgentStatusTone,
  today = moment(),
): StatusHistoryDay[] {
  const outages: Record<number, StatusOutage[]> =
    channel === `voice`
      ? {
          22: [{ tone: `redirecting`, fromMinute: 13 * 60 + 5, toMinute: 17 * 60 + 15 }],
          9: [{ tone: `interrupted`, fromMinute: 9 * 60 + 40, toMinute: 10 * 60 + 25 }],
          3: [{ tone: `redirecting`, fromMinute: 0, toMinute: FULL_DAY }],
        }
      : { 15: [{ tone: `offline`, fromMinute: 14 * 60, toMinute: 16 * 60 }] }
  return Array.from({ length: STATUS_HISTORY_DAYS }, (_, i) => {
    const daysAgo = STATUS_HISTORY_DAYS - 1 - i
    const date = today.clone().subtract(daysAgo, `days`).format(`YYYY-MM-DD`)
    if (daysAgo === 0) return { date, tone: current, outages: [] }
    const dayOutages = outages[daysAgo] ?? []
    return { date, tone: worstTone(dayOutages), outages: dayOutages }
  })
}

/** The agent basics a practice edits on My Agent, as stored (greeting as its template). */
export type AgentBasics = {
  agentName: string
  businessName: string
  greeting: string
  businessAddress: string
  officePhone: string
  /** The location's own transfer number; empty means calls go to the office phone. Super users edit it. */
  transferPhone: string
}

/** A phone as the API stores it: digits only, no leading +1. */
export const phoneDigits = (phone: string | null | undefined): string =>
  (phone ?? ``).replace(/^\+1/, ``).replace(/\D/g, ``)

/**
 * The update for what changed and nothing else. The office phone lives on the location and needs
 * the location settings permission on the server, so an unchanged phone must never be sent.
 */
export function basicsUpdate(
  draft: AgentBasics,
  saved: AgentBasics,
): Partial<
  Record<
    `agentName` | `businessName` | `greeting` | `businessAddress` | `officeMainNumber` | `officeTransferNumber`,
    string
  >
> {
  const update: ReturnType<typeof basicsUpdate> = {}
  for (const key of [`agentName`, `businessName`, `greeting`, `businessAddress`] as const) {
    if (draft[key].trim() !== saved[key].trim()) update[key] = draft[key].trim()
  }
  if (phoneDigits(draft.officePhone) !== phoneDigits(saved.officePhone)) {
    update.officeMainNumber = phoneDigits(draft.officePhone)
  }
  if (phoneDigits(draft.transferPhone) !== phoneDigits(saved.transferPhone)) {
    update.officeTransferNumber = phoneDigits(draft.transferPhone)
  }
  return update
}

const SPOKEN_SAMPLES: Record<string, string> = {
  day: `Monday`,
  month: `January`,
  date_as_words: `fifteenth`,
  time_as_words: `three thirty`,
  'AM/PM': `PM`,
}

/** A post-booking line as a caller hears it, with a sample visit in place of the <…> parts. */
export function renderPostBookingSample(message: string): string {
  return message.replace(/<([^>]+)>/g, (whole, key: string) => SPOKEN_SAMPLES[key] ?? whole)
}

/**
 * The <…> parts of a post-booking line, as the prompt writes them, with the chip each becomes in
 * the editor. The chip editor speaks {{…}} tokens, so `key` is the token and `tag` the stored text.
 */
export const POST_BOOKING_TAGS = [
  { tag: `day`, key: `day`, label: `Day` },
  { tag: `month`, key: `month`, label: `Month` },
  { tag: `date_as_words`, key: `date`, label: `Date` },
  { tag: `time_as_words`, key: `time`, label: `Time` },
  { tag: `AM/PM`, key: `ampm`, label: `AM/PM` },
]

/** A stored post-booking line as the chip editor reads it: <tags> become {{tokens}}. */
export function postBookingToEditor(message: string): string {
  return POST_BOOKING_TAGS.reduce((text, part) => text.split(`<${part.tag}>`).join(`{{${part.key}}}`), message)
}

/** The chip editor's text back as the prompt writes it: {{tokens}} become <tags>. */
export function postBookingFromEditor(text: string): string {
  return POST_BOOKING_TAGS.reduce((out, part) => out.split(`{{${part.key}}}`).join(`<${part.tag}>`), text)
}

const OPEN_HOURS_DAYS = [`sunday`, `monday`, `tuesday`, `wednesday`, `thursday`, `friday`, `saturday`] as const
type OpenHoursLikeInput = Partial<
  Record<
    (typeof OPEN_HOURS_DAYS)[number],
    { open?: boolean | null; hours?: { from: string; to: string }[] | null } | null
  >
>

/** Weekly hours in the shape the location update takes: no GraphQL __typename, days that are set only. */
export function toOpenHoursInput(hours: OpenHoursLikeInput | null | undefined): LocationOpenHoursInput {
  const result: LocationOpenHoursInput = {}
  for (const day of OPEN_HOURS_DAYS) {
    const value = hours?.[day]
    if (!value) continue
    result[day] = {
      open: !!value.open,
      hours: (value.hours ?? []).map((range) => ({ from: range.from, to: range.to })),
    }
  }
  return result
}
